import { useEffect, useState } from "react";
import tw from "tailwind-styled-components";
import { MdClose } from "react-icons/md";
import "react-datepicker/dist/react-datepicker.css";
import { capitalizeFirstLetter } from "../../helpers";
import moment from "moment";
import { listing_type, landlord_property_type } from "../../Config";
import { propertyAvailabilityStatus } from "../../Config";
import { FaRegBuilding } from "react-icons/fa";

const ViewInventoriesModel = ({ apiData, handleClose, selectedData }) => {
  const [height, setHeight] = useState(window.innerHeight);

  useEffect(() => {
    const handleResize = () => {
      setHeight(window.innerHeight);
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Helper function to get status color
  const getStatusColor = (status) => {
    const colors = {
      Available: "bg-green-100 text-green-700 border-green-100",
      Closed: "bg-red-100 text-red-700 border-red-100",
      Leased: "bg-blue-100 text-blue-700 border-blue-100",
      Hold: "bg-yellow-100 text-yellow-700 border-yellow-100",
      Unverified: "bg-gray-100 text-gray-700 border-gray-100",
    };
    return colors[status] || "bg-gray-100 text-gray-700 border-gray-100";
  };

  const getLabelFromValue = (options, value) => {
    const found = options.find((item) => item.value === value);
    return found ? found.label : "-";
  };

  return (
    <Container
      style={{
        height: `${height}px`,
        overflow: "auto",
        WebkitOverflowScrolling: "touch",
      }}
    >
      <Header>
        <h1 className="text-white text-lg font-medium">Inventories</h1>
        <button onClick={handleClose}>
          <MdClose color="white" size={24} />
        </button>
      </Header>

      <ProfileSection className="px-4 py-3 flex items-center justify-between">
        {/* Left: Icon + Info */}
        <div className="flex items-center gap-2">
          {/* Inventory Icon */}
          <div className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center">
            <FaRegBuilding className="text-gray-600" />
          </div>

          {/* Text Info */}
          <div className="flex flex-col">
            <p className="text-md font-medium text-gray-800">
              {capitalizeFirstLetter(selectedData?.name || "")}
            </p>
            <p className="text-xs text-gray-400">
              {"#" + selectedData?.mobile}
            </p>
          </div>
        </div>

        {/* Right: Budget */}
        <div className="text-right">
          <p className="text-xs text-gray-400">Inventories</p>
          <p className="text-lg font-bold text-green-600">
            {selectedData?.inventoryCount || 0}
          </p>
        </div>
      </ProfileSection>

      {/* Inventories List */}
      <Content>
        {apiData?.length > 0 ? (
          apiData.map((inventory, index) => {
            const statusLabel = getLabelFromValue(
              propertyAvailabilityStatus,
              inventory.status,
            );
            const statusColor = getStatusColor(statusLabel);
            return (
              <PropertyCard key={inventory.id || index}>
                <CardHeader>
                  <PropertyInfo>
                    <InventoryName>
                      {capitalizeFirstLetter(inventory.inventoryName)}
                    </InventoryName>
                    <InventoryType>{inventory.address}</InventoryType>
                  </PropertyInfo>
                  <StatusBadge className={statusColor}>
                    {statusLabel}
                  </StatusBadge>
                </CardHeader>

                <Divider />

                <PropertyDetails>
                  <DetailRow>
                    <DetailItem>
                      <Label>Listing Type</Label>
                      <Value>
                        {capitalizeFirstLetter(
                          getLabelFromValue(
                            listing_type,
                            inventory.listingType,
                          ),
                        )}
                      </Value>
                    </DetailItem>
                    <DetailItem>
                      <Label>Inventory Type</Label>
                      <Value>
                        {capitalizeFirstLetter(
                          getLabelFromValue(
                            landlord_property_type,
                            inventory.inventoryType,
                          ),
                        )}
                      </Value>
                    </DetailItem>
                  </DetailRow>
                  <DetailRow>
                    <DetailItem>
                      <Label>Monthly Rent</Label>
                      <Value className="font-medium">
                        {/* {formatCurrency(inventory.rent)} */}₹
                        {inventory.amount || 0}
                      </Value>
                    </DetailItem>

                    <DetailItem>
                      <Label>Security Deposit</Label>
                      <Value>₹{inventory.deposit || 0}</Value>
                    </DetailItem>
                  </DetailRow>

                  <MetaInfo>
                    <span className="text-xs text-gray-400 ml-3">
                      Last Updated:{" "}
                      {moment(inventory.updatedAt).format("DD MMM YY")}
                    </span>
                  </MetaInfo>
                </PropertyDetails>
              </PropertyCard>
            );
          })
        ) : (
          <EmptyState>
            <p className="text-gray-400">
              No inventory available for this broker
            </p>
          </EmptyState>
        )}
      </Content>
    </Container>
  );
};

// Styled Components
const Container = tw.div`bg-white w-full h-full flex flex-col relative`;
const Header = tw.div`flex items-center justify-between bg-gradient-to-r from-primary-color to-teal-400 py-4 px-4`;
const ProfileSection = tw.div`flex items-center gap-3 bg-gray-100 px-4 p-4`;
const Content = tw.div`px-4 py-4 space-y-4 overflow-y-auto flex-1`;
const PropertyCard = tw.div`bg-white border border-gray-200 rounded-lg p-4`;
const CardHeader = tw.div`flex justify-between items-start mb-3`;
const PropertyInfo = tw.div`flex flex-col`;
const InventoryName = tw.div`flex items-center text-base font-medium text-gray-800`;
const InventoryType = tw.div`flex items-center text-sm text-gray-400`;
const StatusBadge = tw.span`text-xs px-2 py-1 rounded-full font-semibold whitespace-nowrap`;
const Divider = tw.div`h-px bg-gray-100 my-3`;
const PropertyDetails = tw.div`space-y-3`;
const DetailRow = tw.div`grid grid-cols-2 gap-4`;
const DetailItem = tw.div`flex flex-col`;
const Label = tw.label`text-sm text-gray-400 font-normal`;
const Value = tw.span`text-gray-600 text-sm md:text-base`;
const MetaInfo = tw.div`flex justify-end mt-3 pt-2`;
const EmptyState = tw.div`text-center text-gray-500 text-sm py-10`;

export default ViewInventoriesModel;
